import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { pluginApi, serverApi } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApi';
import { Loader2, Save, BookOpen, Plus, Trash2, Eye, Code, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

interface EmbedField {
  name: string;
  value: string;
  inline: boolean;
}

interface RulesConfig {
  enabled: boolean;
  title: string;
  description: string;
  color: string;
  fields: EmbedField[];
  footer: string;
  timestamp: boolean;
  channelId: string;
  autoPost: boolean;
  postOnJoin: boolean;
}

export function RulesPlugin({ serverId }: { serverId: string }) {
  const { data: plugins } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const { data: channels = [] } = useQuery({
    queryKey: ['servers', serverId, 'channels'],
    queryFn: () => serverApi.getChannels(serverId),
  });

  const rulesPlugin = (plugins as any)?.find((p: any) => p.pluginId === 'rules');
  const isEnabled = rulesPlugin?.enabled ?? false;

  const { mutate: togglePlugin } = useApiMutation(
    ({ enabled }: { enabled: boolean }) => 
      enabled ? pluginApi.enablePlugin(serverId, 'rules') : pluginApi.disablePlugin(serverId, 'rules'),
    { invalidateQueries: [['servers', serverId, 'plugins']] }
  );

  const { mutate: updateConfig, isPending: saving } = useApiMutation(
    (config: any) => pluginApi.updatePluginConfig(serverId, 'rules', config),
    { 
      onSuccess: () => toast.success('Regolamento salvato!'),
      invalidateQueries: [['servers', serverId, 'plugins']] 
    }
  );

  const [config, setConfig] = useState<RulesConfig>({
    enabled: true,
    title: '📋 Regolamento del Server',
    description: 'Benvenuto! Per mantenere il nostro server ordinato e piacevole, leggi attentamente le seguenti regole.',
    color: '#5865F2',
    fields: [
      { name: '1️⃣ Rispetto Reciproco', value: 'Tratta gli altri come vorresti essere trattato. Nessun insulto, molestia o discriminazione.', inline: false },
      { name: '2️⃣ Linguaggio Appropriato', value: 'Evita parolacce eccessive e linguaggio offensivo. Mantieni una comunicazione civile.', inline: false },
      { name: '3️⃣ Spam e Pubblicità', value: 'Nessuno spam, link sospetti o pubblicità senza autorizzazione.', inline: false },
    ],
    footer: '© Gestito da Community Hub Bot',
    timestamp: true,
    channelId: '',
    autoPost: false,
    postOnJoin: false,
  });

  useEffect(() => {
    if (rulesPlugin?.config) {
      setConfig({ ...config, ...rulesPlugin.config });
    }
  }, [rulesPlugin]);

  const handleAddField = () => {
    setConfig({
      ...config,
      fields: [...config.fields, { name: '', value: '', inline: false }],
    });
  };

  const handleRemoveField = (index: number) => {
    setConfig({
      ...config,
      fields: config.fields.filter((_, i) => i !== index),
    });
  };

  const handleUpdateField = (index: number, field: Partial<EmbedField>) => {
    const newFields = [...config.fields];
    newFields[index] = { ...newFields[index], ...field };
    setConfig({ ...config, fields: newFields });
  };

  const handleSave = () => {
    if (!config.title.trim()) {
      toast.error('Il titolo è obbligatorio');
      return;
    }
    updateConfig(config);
  };

  const embedPreview = {
    title: config.title,
    description: config.description,
    color: config.color.replace('#', ''),
    fields: config.fields,
    footer: config.footer ? { text: config.footer } : undefined,
    timestamp: config.timestamp ? new Date().toISOString() : undefined,
  };

  if (!rulesPlugin) return null;

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-12">
      {/* Header */}
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-purple-500/20 rounded-xl">
            <BookOpen className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h3 className="font-bold text-xl">Regolamento Embed</h3>
            <p className="text-sm text-muted-foreground">
              Crea e personalizza embed per il regolamento del server.
            </p>
          </div>
        </div>
        <Switch
          checked={isEnabled}
          onCheckedChange={(enabled) => togglePlugin({ enabled })}
        />
      </div>

      {isEnabled && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Editor */}
          <div className="space-y-6">
            {/* Contenuto Base */}
            <div className="glass-card p-6 space-y-4">
              <h4 className="font-semibold flex items-center gap-2">
                <Code className="w-4 h-4" />
                Contenuto Base
              </h4>

              <div className="space-y-3">
                <div>
                  <Label htmlFor="title" className="text-sm mb-2 block">Titolo</Label>
                  <Input
                    id="title"
                    value={config.title}
                    onChange={(e) => setConfig({ ...config, title: e.target.value })}
                    placeholder="es: 📋 Regolamento del Server"
                    className="bg-sidebar-accent border-sidebar-border"
                  />
                </div>

                <div>
                  <Label htmlFor="description" className="text-sm mb-2 block">Descrizione</Label>
                  <Textarea
                    id="description"
                    value={config.description}
                    onChange={(e) => setConfig({ ...config, description: e.target.value })}
                    placeholder="Descrizione principale del regolamento..."
                    rows={3}
                    className="bg-sidebar-accent border-sidebar-border resize-none"
                  />
                </div>

                <div className="flex gap-3">
                  <div className="flex-1">
                    <Label htmlFor="color" className="text-sm mb-2 block">Colore</Label>
                    <div className="flex gap-2">
                      <Input
                        id="color"
                        type="color"
                        value={config.color}
                        onChange={(e) => setConfig({ ...config, color: e.target.value })}
                        className="w-16 h-10 bg-sidebar-accent border-sidebar-border cursor-pointer"
                      />
                      <Input
                        type="text"
                        value={config.color}
                        onChange={(e) => setConfig({ ...config, color: e.target.value })}
                        placeholder="#5865F2"
                        className="flex-1 bg-sidebar-accent border-sidebar-border"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Regole (Fields) */}
            <div className="glass-card p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold">Regole</h4>
                <Button
                  onClick={handleAddField}
                  size="sm"
                  variant="outline"
                  className="gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Aggiungi
                </Button>
              </div>

              <div className="space-y-4">
                {config.fields.map((field, index) => (
                  <div key={index} className="p-4 bg-sidebar-accent rounded-lg space-y-3 border border-sidebar-border">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-mono text-muted-foreground">Regola {index + 1}</span>
                      <Button
                        onClick={() => handleRemoveField(index)}
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    <div>
                      <Label className="text-xs mb-1 block">Titolo</Label>
                      <Input
                        value={field.name}
                        onChange={(e) => handleUpdateField(index, { name: e.target.value })}
                        placeholder="es: 1️⃣ Rispetto Reciproco"
                        className="bg-background border-border text-sm"
                      />
                    </div>

                    <div>
                      <Label className="text-xs mb-1 block">Descrizione</Label>
                      <Textarea
                        value={field.value}
                        onChange={(e) => handleUpdateField(index, { value: e.target.value })}
                        placeholder="Descrizione della regola..."
                        rows={2}
                        className="bg-background border-border text-sm resize-none"
                      />
                    </div>

                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={field.inline}
                        onChange={(e) => handleUpdateField(index, { inline: e.target.checked })}
                        className="rounded"
                      />
                      <span>Inline (visualizza affiancato)</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer e Impostazioni */}
            <div className="glass-card p-6 space-y-4">
              <h4 className="font-semibold">Impostazioni Avanzate</h4>

              <div>
                <Label htmlFor="footer" className="text-sm mb-2 block">Footer</Label>
                <Input
                  id="footer"
                  value={config.footer}
                  onChange={(e) => setConfig({ ...config, footer: e.target.value })}
                  placeholder="es: © Community Hub Bot"
                  className="bg-sidebar-accent border-sidebar-border"
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <Switch
                  checked={config.timestamp}
                  onCheckedChange={(timestamp) => setConfig({ ...config, timestamp })}
                />
                <span className="text-sm">Mostra timestamp</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <Switch
                  checked={config.autoPost}
                  onCheckedChange={(autoPost) => setConfig({ ...config, autoPost })}
                />
                <span className="text-sm">Pubblica automaticamente al salvataggio</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <Switch
                  checked={config.postOnJoin}
                  onCheckedChange={(postOnJoin) => setConfig({ ...config, postOnJoin })}
                />
                <span className="text-sm">Invia al nuovo utente</span>
              </label>

              <div>
                <Label htmlFor="channel" className="text-sm mb-2 block">Canale di Pubblicazione</Label>
                <Select
                  value={config.channelId || 'none'}
                  onValueChange={(value) => setConfig({ ...config, channelId: value === 'none' ? '' : value })}
                >
                  <SelectTrigger id="channel" className="bg-sidebar-accent border-sidebar-border">
                    <SelectValue placeholder="Seleziona canale di pubblicazione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun canale</SelectItem>
                    {(channels as any[]).map((channel: any) => (
                      <SelectItem key={channel.id} value={channel.id}>#{channel.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  <Save className="w-4 h-4" />
                  Salva Regolamento
                </Button>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="sticky top-20">
            <div className="glass-card p-6 space-y-4">
              <h4 className="font-semibold flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Anteprima
              </h4>

              {/* Discord Embed Preview */}
              <div 
                className="rounded-lg overflow-hidden border-l-4"
                style={{ borderColor: config.color }}
              >
                <div className="bg-discord-dark p-4 space-y-3">
                  {/* Title */}
                  <div className="font-bold text-white text-lg">
                    {config.title || 'Titolo'}
                  </div>

                  {/* Description */}
                  {config.description && (
                    <div className="text-discord-text text-sm whitespace-pre-wrap break-words">
                      {config.description}
                    </div>
                  )}

                  {/* Fields */}
                  {config.fields.length > 0 && (
                    <div className={`grid gap-4 ${config.fields.some(f => f.inline) ? 'grid-cols-2' : 'grid-cols-1'}`}>
                      {config.fields.map((field, idx) => (
                        field.name && (
                          <div key={idx} className={field.inline ? 'col-span-1' : 'col-span-full'}>
                            <div className="font-semibold text-white text-sm mb-1">
                              {field.name}
                            </div>
                            <div className="text-discord-text text-sm whitespace-pre-wrap break-words">
                              {field.value || '—'}
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  )}

                  {/* Footer */}
                  {config.footer && (
                    <div className="text-discord-footer text-xs pt-2 border-t border-discord-border">
                      {config.footer}
                      {config.timestamp && (
                        <div className="text-discord-footer text-xs">
                          {new Date().toLocaleDateString('it-IT')} {new Date().toLocaleTimeString('it-IT')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Info Box */}
              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg flex gap-3">
                <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-blue-300 space-y-1">
                  <p className="font-semibold">Informazioni Preview:</p>
                  <ul className="list-disc list-inside space-y-0.5">
                    <li>Visualizza come apparirà su Discord</li>
                    <li>Massimo 25 campi per embed</li>
                    <li>Massimo 2048 caratteri per campo</li>
                    <li>Colore in formato HEX (#RRGGBB)</li>
                  </ul>
                </div>
              </div>

              {/* JSON Export */}
              <div className="p-4 bg-sidebar-accent rounded-lg border border-sidebar-border">
                <p className="text-xs font-mono text-muted-foreground mb-2">JSON Export:</p>
                <pre className="text-xs overflow-auto max-h-64 text-muted-foreground">
                  {JSON.stringify(embedPreview, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
